import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { CustomToggle } from '../components/CustomToggle';
import { TermsAndConditionsContent } from '../components/TermsAndConditionsContent';
import { SignupProgress, USER_SIGNUP_STEPS } from '../components/SignupProgress';
import { authApiService } from '../services/authApiService';
import { debugError } from '../utils/debug';

export const ApplicantTeamInvitePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { flags, isLoading: flagsLoading } = useFeatureFlagContext();
    const selfServeSignup = flags['SelfServiceSignup'] ?? false;

    const token = (searchParams.get('token') || '').trim();
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [checking, setChecking] = useState(true);
    const [redirectTo, setRedirectTo] = useState<string | null>(null);

    useEffect(() => {
        document.title = 'Join your team | Calculist';
    }, []);

    useEffect(() => {
        if (!token || flagsLoading || !selfServeSignup) return;
        authApiService.signupCheckTeamInvite(token)
            .then(res => {
                if (res.data.status === 'alreadyMember' && res.data.email) {
                    setRedirectTo(`/auth/login?email=${encodeURIComponent(res.data.email)}`);
                }
            })
            .catch(err => debugError('signupCheckTeamInvite', err))
            .finally(() => setChecking(false));
    }, [token, flagsLoading, selfServeSignup]);

    if (flagsLoading || checking) return null;
    if (!selfServeSignup) return <Navigate to="/auth/login" replace />;
    if (!token) return <Navigate to="/auth/login" replace />;
    if (redirectTo) return <Navigate to={redirectTo} replace />;

    const wrapper = (content: React.ReactNode) => (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
        }}>
            {content}
        </div>
    );

    const handleAccept = () => {
        if (!acceptTerms) {
            setError('You must accept the terms and conditions to continue.');
            return;
        }
        navigate(`/auth/complete-team-invite/password?token=${encodeURIComponent(token)}`);
    };

    return wrapper(
        <div style={{ ...themedStyles.card.default, maxWidth: '560px', width: '100%' }}>
            <SignupProgress currentStep={1} steps={USER_SIGNUP_STEPS} />

            <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                <img src="/logo-name.svg" alt="Calculist" style={{ height: '64px', width: 'auto', marginBottom: theme.spacing.md }} />
                <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>Review &amp; Accept Terms</h1>
                <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>Please review and accept the terms to join your team.</p>
            </div>

            <div style={{
                maxHeight: '320px',
                overflowY: 'auto',
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: theme.borderRadius.lg,
                padding: theme.spacing.md,
                marginBottom: theme.spacing.lg,
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.text.secondary,
                lineHeight: 1.6,
            }}>
                <TermsAndConditionsContent />
            </div>

            <div style={{ marginBottom: theme.spacing.lg }}>
                <CustomToggle
                    checked={acceptTerms}
                    onChange={(v) => { setAcceptTerms(v); if (v) setError(null); }}
                    label="I have read and accept the Terms and Conditions"
                />
            </div>

            {error && (
                <span style={{ display: 'block', fontSize: theme.typography.fontSize.xs, color: theme.colors.status.error, marginBottom: theme.spacing.sm }}>{error}</span>
            )}

            <button
                type="button"
                onClick={handleAccept}
                disabled={!acceptTerms}
                style={{
                    ...themedStyles.button.primary,
                    ...(!acceptTerms ? themedStyles.button.disabled : {}),
                    width: '100%',
                    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                }}
            >
                Accept Terms
            </button>
        </div>
    );
};
